<template>
  <oare-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    title="Change Side Designation"
    :persistent="false"
    :submitLoading="editSideLoading"
    :submitDisabled="!formComplete"
    @submit="editSide"
  >
    <v-row class="ma-0"
      >Select the side you would like to edit and then select the side
      designation you would like to change it to.</v-row
    >
    <v-row class="ma-0 mt-4">Side You Want to Edit</v-row>
    <v-row class="ma-0">
      <v-select
        :items="alreadyUsedSides"
        v-model="originalSide"
        variant="outlined"
        density="compact"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props">
            <template #title>
              {{ item }}
              <v-menu v-if="item === 'obv. ii'" open-on-hover location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" class="ml-1 mr-n2" size="small">
                    mdi-information-outline
                  </v-icon>
                </template>
                <v-card class="pa-3">
                  <span>
                    For use only when writer continues from u.e. onto obv. near
                    end of text.
                  </span>
                </v-card>
              </v-menu>
            </template>
          </v-list-item>
        </template></v-select
      >
    </v-row>

    <v-row class="ma-0">New Designation for Side</v-row>
    <v-row class="ma-0">
      <v-select
        :items="usableSides"
        v-model="newSide"
        variant="outlined"
        density="compact"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props">
            <template #title>
              {{ item }}
              <v-menu v-if="item === 'obv. ii'" open-on-hover location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" class="ml-1 mr-n2" size="small">
                    mdi-information-outline
                  </v-icon>
                </template>
                <v-card class="pa-3">
                  <span>
                    For use only when writer continues from u.e. onto obv. near
                    end of text.
                  </span>
                </v-card>
              </v-menu>
            </template>
          </v-list-item>
        </template>
      </v-select>
    </v-row>
  </oare-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, PropType } from 'vue';
import { EditSidePayload, EpigraphicUnitSide } from '@oare/types';
import sl from '@/serviceLocator';

export default defineComponent({
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    textUuid: {
      type: String,
      required: true,
    },
    usableSides: {
      type: Array as PropType<EpigraphicUnitSide[]>,
      required: true,
    },
    alreadyUsedSides: {
      type: Array as PropType<EpigraphicUnitSide[]>,
      required: true,
    },
  },
  emits: [
    'update:modelValue',
    'reset-renderer',
    'reset-current-edit-action',
    'selected-side',
  ],
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const editSideLoading = ref(false);

    const originalSide = ref<EpigraphicUnitSide | null>(null);
    const newSide = ref<EpigraphicUnitSide | null>(null);

    const formComplete = computed(() => originalSide.value && newSide.value);

    const editSide = async () => {
      try {
        editSideLoading.value = true;

        if (!originalSide.value || !newSide.value) {
          throw new Error('Required side designations not provided');
        }

        const payload: EditSidePayload = {
          type: 'editSide',
          textUuid: props.textUuid,
          originalSide: originalSide.value,
          newSide: newSide.value,
        };
        await server.editText(payload);
        emit('reset-renderer');
        emit('selected-side', newSide.value);
      } catch (err) {
        actions.showErrorSnackbar(
          'Error editing side designation. Please try again.',
          err as Error
        );
      } finally {
        editSideLoading.value = false;
        emit('reset-current-edit-action');
        emit('update:modelValue', false);
      }
    };

    return {
      editSideLoading,
      originalSide,
      newSide,
      formComplete,
      editSide,
    };
  },
});
</script>
